import { forwardRef } from "react";
import { Link as MuiLink, type LinkProps as MuiLinkProps } from "@mui/material";

import { brand } from "../../theme/brand";
import { paletteVarAlpha } from "../../theme/palette-var";

import type { SxProps, Theme } from "@mui/material/styles";

/** Token-bound link colors. */
export type LinkColor = "primary" | "inherit";

/** Locked underline behaviors. */
export type LinkUnderline = "always" | "hover" | "none";

export interface LinkProps extends Omit<MuiLinkProps, "color" | "underline"> {
  /** Text color, bound to a theme token. Default: "primary". */
  color?: LinkColor;
  /** Underline behavior. Default: "always". */
  underline?: LinkUnderline;
  /** Destination when `component` is a router link; passed through untouched. */
  to?: string;
  /** Root test id. Default: "ds-link". */
  "data-testid"?: string;
}

/**
 * `primary` resolves through `palette.brand` rather than MUI's `primary`, which
 * holds the supporting teal in the app's theme and the brand purple here.
 *
 * MUI derives the underline from `--Link-underlineColor`, which it sets from
 * `palette.primary.main` — the teal — so the property has to be restated
 * alongside the text colour in every state. Its transparency runs in CSS
 * because `brand()` can return a custom property, which `alpha()` cannot parse.
 */
const brandSx: SxProps<Theme> = {
  color: brand("primary"),
  "--Link-underlineColor": (t: Theme) =>
    paletteVarAlpha(brand("primary")(t), 0.4),
  "&:hover": {
    color: brand("primaryHover"),
    "--Link-underlineColor": (t: Theme) =>
      paletteVarAlpha(brand("primaryHover")(t), 0.4),
  },
};

const colorSx: Record<LinkColor, SxProps<Theme> | undefined> = {
  primary: brandSx,
  inherit: undefined,
};

/** Constrains MUI Link to the locked Centro color and underline set. */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      color = "primary",
      underline = "always",
      variant = "inherit",
      component = "a",
      type,
      sx,
      "data-testid": dataTestId = "ds-link",
      ...rest
    },
    ref,
  ) => (
    <MuiLink
      ref={ref}
      data-testid={dataTestId}
      color={color === "inherit" ? "inherit" : undefined}
      sx={[colorSx[color], sx] as SxProps<Theme>}
      underline={underline}
      variant={variant}
      component={component}
      type={component === "button" ? (type ?? "button") : type}
      {...rest}
    />
  ),
);

Link.displayName = "Link";
